import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Tag } from 'rsuite';
import { get, getOr, last, split } from 'lodash/fp';
import { DragDrop } from '@uppy/react';
import { useDispatch, useSelector } from 'react-redux';
import uppyAwsS3Upload from '../../helpers/uppyAwsS3Upload';
import { ClientGuid, AppConfig } from '../../helpers/config';
import { setIsUploadingFile } from '../../redux/Guest/actions/components/guest-form';
import { getIsUploadingFile } from '../../redux/Guest/selectors/components/guest-form';
import { StyledPanel, StyledProgressLine, UploadDisabler } from './elements';

const BaseDir = `${ClientGuid}/clients`;

const CFFileUpload = ({ onFileSelect, ...props }) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [uppyUpload, setUppyUpload] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const isUploading = useSelector(getIsUploadingFile);
  const savingInProgress = !!uploadProgress;
  const uploadStatus = uploadProgress < 100 ? 'active' : 'success';
  const value = getOr('', 'value', props);

  const resetFileUpload = () => {
    if (uppyUpload) uppyUpload.reset();
    setFile(null);
    setUploadProgress(null);
    setFileName('');
  };

  useEffect(() => {
    setUppyUpload(
      uppyAwsS3Upload({
        baseDir: BaseDir,
        bucket: get('buckets.sbd', AppConfig),
        onFileAdded: ({ data: { name } }) => {
          setFile(name.replace(/ /g, '_'));
        },
        onUpload: () => {
          setUploadProgress(1);
          dispatch(setIsUploadingFile(true));
        },
        onUploadProgress: (_, { bytesUploaded, bytesTotal }) => {
          setUploadProgress((100 * bytesUploaded) / bytesTotal);
        },
        onUploadError: () => {
          resetFileUpload();
          dispatch(setIsUploadingFile(false));
        },
        onComplete: result => {
          const fileUrl = get('successful.0.uploadURL', result);
          const name = last(split('/', fileUrl));

          setFileName(`${BaseDir}/${name}`);
          dispatch(setIsUploadingFile(false));
        }
      })
    );
  }, []);

  useEffect(
    () => {
      if (file && !value) {
        uppyUpload.upload();
      }
    },
    [file]
  );

  useEffect(
    () => {
      onFileSelect(fileName);
    },
    [fileName]
  );

  useEffect(
    () => {
      const name = last(split('/', value));
      if (name) {
        setFile(name);
      }
    },
    [value]
  );

  return (
    <StyledPanel bordered>
      {file ? (
        <Fragment>
          <Tag onClick={resetFileUpload} closable={!savingInProgress}>
            {file}
          </Tag>
          {uploadProgress && (
            <StyledProgressLine
              status={uploadStatus}
              percent={Math.round(uploadProgress)}
            />
          )}
        </Fragment>
      ) : (
        <Fragment>
          {isUploading && <UploadDisabler />}
          {uppyUpload && (
            <DragDrop
              height="140px"
              note={get('opts.note', uppyUpload)}
              uppy={uppyUpload}
            />
          )}
        </Fragment>
      )}
    </StyledPanel>
  );
};

CFFileUpload.propTypes = {
  onFileSelect: PropTypes.func.isRequired
};

export default CFFileUpload;
